import React, { useState, useRef, useEffect, useCallback } from 'react';
import './monthRangeSelector.css';

const monthLabels = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Juin',
  'Juil',
  'Août',
  'Sept',
  'Oct',
  'Nov',
  'Déc',
];

const currentYear = new Date().getFullYear();
const startYear = 2020;
const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i);

const totalMonths = monthLabels.length * years.length;

const monthWidth = 60;

const datesToRange = (start, end) => {
  const startIndex = (start.getFullYear() - startYear) * 12 + start.getMonth();
  const endIndex = (end.getFullYear() - startYear) * 12 + end.getMonth();
  return [
    Math.max(0, Math.min(startIndex, totalMonths - 1)),
    Math.max(0, Math.min(endIndex, totalMonths - 1)),
  ];
};

const rangeToDates = range => {
  const sYear = Math.floor(range[0] / 12) + startYear;
  const sMonth = range[0] % 12;
  const eYear = Math.floor(range[1] / 12) + startYear;
  const eMonth = range[1] % 12;
  return {
    startDate: new Date(sYear, sMonth, 1),
    endDate: new Date(eYear, eMonth + 1, 0),
  };
};

const MonthRangeSelector = ({ startDate, endDate, setStartDate, setEndDate }) => {
  const [range, setRange] = useState(() => datesToRange(startDate, endDate));
  const scrollRef = useRef(null);
  const cursorRef = useRef(null);
  const isDragging = useRef(false);
  const dragIndex = useRef(null);
  const isInternalChange = useRef(false);

  // Scroll initial vers le début de la sélection
  useEffect(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      container.scrollLeft = (container.scrollWidth * (range[0] - 1)) / totalMonths;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync du range quand les dates changent depuis l'extérieur
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    setRange(datesToRange(startDate, endDate));
  }, [startDate, endDate]);

  const commitDates = useCallback(
    newRange => {
      isInternalChange.current = true;
      const { startDate: newStart, endDate: newEnd } = rangeToDates(newRange);
      setStartDate(newStart);
      setEndDate(newEnd);
    },
    [setStartDate, setEndDate]
  );

  const isMonthInRange = (year, monthIndex) => {
    const index = (year - startYear) * 12 + monthIndex;
    return index >= range[0] && index <= range[1];
  };

  const handleMouseDown = index => () => {
    isDragging.current = true;
    dragIndex.current = index;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = event => {
    if (!isDragging.current) return;

    const offsetX = event.clientX - cursorRef.current.getBoundingClientRect().left;
    let newValue = Math.round(offsetX / monthWidth);

    if (dragIndex.current === 0) {
      newValue = Math.max(0, Math.min(newValue, range[1]));
      setRange([newValue, range[1]]);
    }

    if (dragIndex.current === 1) {
      newValue = Math.min(totalMonths - 1, Math.max(newValue - 1, range[0]));
      setRange([range[0], newValue]);
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    dragIndex.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    setRange(currentRange => {
      commitDates(currentRange);
      return currentRange;
    });
  };

  const handleClick = monthYearIndex => {
    const newRange = [monthYearIndex, monthYearIndex];
    setRange(newRange);
    commitDates(newRange);
  };

  return (
    <div ref={scrollRef} className="month-range-selector">
      {years.map((year, indexY) => (
        <div key={year} className="year-section">
          <div>{year}</div>
          <div className="months-row">
            {monthLabels.map((month, indexM) => (
              <div key={month} className="month-section">
                <div>{month}</div>
                <div
                  className={`month-rectangle ${
                    isMonthInRange(year, indexM) ? 'month-selected' : ''
                  }`}
                  onClick={() => handleClick(indexY * monthLabels.length + indexM)}
                ></div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="cursor-wrapper" ref={cursorRef}>
        <div
          className="cursor left-cursor"
          style={{ left: `${range[0] * monthWidth}px` }}
          onMouseDown={handleMouseDown(0)}
        ></div>
        <div
          className="cursor right-cursor"
          style={{ left: `${(range[1] + 1) * monthWidth + 10}px` }}
          onMouseDown={handleMouseDown(1)}
        ></div>
      </div>
    </div>
  );
};

export default MonthRangeSelector;
